import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd/es/button';
import { forwardRef } from 'react';
import { usePermissionStore } from '../../../stores/permission.store';
import './Button.css';

type OmittedProps = Omit<AntButtonProps, 'type' | 'ghost' | 'block' | 'variant'>;

export interface ButtonProps extends OmittedProps {
  /**
   * Button variant
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'ghost' | 'text' | 'link';
  /**
   * Full width button
   * @default false
   */
  fullWidth?: boolean;
  /**
   * Permission(s) required to render the button.
   * If the user lacks permission, the button is not rendered.
   */
  permission?: string | string[];
  /**
   * When true, ALL listed permissions are required. Default: any one is enough.
   */
  requireAll?: boolean;
}

/**
 * Modern Button Component
 *
 * Enhanced Ant Design button with modern SaaS styling and built-in permission gating.
 *
 * @example
 * ```tsx
 * <Button variant="primary" permission="iam:user:create">
 *   Create User
 * </Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'middle',
      fullWidth = false,
      permission,
      requireAll = false,
      className,
      ...props
    },
    ref
  ) => {
    const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissionStore();

    // Permission check — skip rendering if user lacks required permission
    if (permission) {
      const perms = Array.isArray(permission) ? permission : [permission];
      const allowed = requireAll
        ? hasAllPermissions(perms)
        : perms.length === 1
          ? hasPermission(perms[0]!)
          : hasAnyPermission(perms);
      if (!allowed) return null;
    }

    const variantMap = {
      primary: 'primary',
      secondary: 'default',
      ghost: 'default',
      text: 'text',
      link: 'link',
    } as const;

    const classNames = [
      'modern-button',
      `modern-button--${variant}`,
      fullWidth && 'modern-button--full-width',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <AntButton
        ref={ref as any}
        type={variantMap[variant]}
        ghost={variant === 'ghost'}
        size={size}
        block={fullWidth}
        className={classNames}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
